import dayjs from 'dayjs';
import { forwardRef, useMemo } from 'react';
import { AnyObjectSchema } from 'yup';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { AccessPermissions } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { FormProvider } from 'core/states';
import { CorporateLocationHelper } from 'corporate/helpers';
import { useAddBomPartsToQuoteContext } from 'quotes/components/AddBomPartsToQuote';
import {
  getContactDefaultLocationsPrefill,
  useAlternateDefaultValues,
} from 'quotes/components/CustomerQuoteUpsert';
import { DEFAULT_VALID_UNTIL_OFFSET } from 'quotes/constants';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { IBaseQuote } from 'quotes/types';

import { ItemsToCopyField } from './enums';
import { SelectItemsToCopyFormFields } from './SelectItemsToCopyFormFields';
import { ICreateCustomerQuoteForm, ICreateVendorQuoteForm } from './types';

interface IProps {
  entityType?: QuoteEntityType;
  addToQuoteType: QuoteType;
  itemsToCopyProps: {
    getSchema: (selectedQuote?: IBaseQuote) => AnyObjectSchema;
    fields: JSX.Element;
    defaultValues?: Record<string, unknown>;
  };
}

export const SelectItemsToCopy = forwardRef(function SelectItemsToCopy(
  { entityType = QuoteEntityType.QUOTE, addToQuoteType, itemsToCopyProps }: IProps,
  ref
) {
  const { selectedContact, selectedQuote } = useAddBomPartsToQuoteContext();

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const omitDefaultSalesLocation = hasPermission(
    AccessPermissions.QUOTE_OMIT_DEFAULT_SALES_LOCATION
  );

  const isCustomerAlternateVersionEnabled =
    isAlternateVersionEnabled && QuoteType.CUSTOMER === addToQuoteType;

  const alternateDefaultValues = useAlternateDefaultValues({
    entityType,
    isAlternateVersionEnabled: isCustomerAlternateVersionEnabled,
  });

  const defaultValues = useMemo(() => {
    const commonDefaultValues = {
      [ItemsToCopyField.PARTS as string]: true,
      attachQuote: true,
      ...itemsToCopyProps.defaultValues,
    };

    if (selectedQuote?.id) {
      return { ...commonDefaultValues };
    }

    // A new order prefills the contact's default addresses only as Create Order does; no project is
    // selected yet, it is picked later in the form.
    const { prefillBillTo, prefillShipTo } =
      QuoteEntityType.ORDER === entityType
        ? getContactDefaultLocationsPrefill({
            billToAddress: selectedContact?.billToAddress,
            shipToAddress: selectedContact?.shipToAddress,
            isAlternateVersionEnabled: isCustomerAlternateVersionEnabled,
            hasProjectSelected: false,
          })
        : { prefillBillTo: true, prefillShipTo: true };

    const validUntilDefaultValue =
      entityType === QuoteEntityType.QUOTE ? dayjs().add(DEFAULT_VALID_UNTIL_OFFSET, 'day') : null;

    return {
      jobName: '',
      contact: selectedContact,
      location:
        !omitDefaultSalesLocation &&
        user?.location &&
        CorporateLocationHelper.isSales(user.location, isAlternateVersionEnabled)
          ? { id: user.location.id, name: user.location.name }
          : null,
      validUntil: validUntilDefaultValue,
      shippingLocation: CorporateLocationHelper.getDefaultShipping(
        [user?.shippingLocation],
        isAlternateVersionEnabled
      ),
      shipToLocation: selectedContact?.id && prefillShipTo ? selectedContact?.shipToAddress : null,
      billToLocation: selectedContact?.id && prefillBillTo ? selectedContact?.billToAddress : null,
      setBillToAsDefaultForContact: false,
      setShipToAsDefaultForContact: false,
      ...alternateDefaultValues,
      ...commonDefaultValues,
    };
  }, [
    itemsToCopyProps.defaultValues,
    selectedQuote?.id,
    entityType,
    selectedContact,
    omitDefaultSalesLocation,
    user.location,
    user.shippingLocation,
    isAlternateVersionEnabled,
    isCustomerAlternateVersionEnabled,
    alternateDefaultValues,
  ]);

  return (
    <FormProvider schema={itemsToCopyProps.getSchema(selectedQuote)} defaultValues={defaultValues}>
      <SelectItemsToCopyFormFields
        ref={ref}
        addToQuoteType={addToQuoteType}
        user={user}
        itemsToCopyFields={itemsToCopyProps.fields}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        defaultValues={defaultValues as ICreateCustomerQuoteForm | ICreateVendorQuoteForm}
        entityType={entityType}
      />
    </FormProvider>
  );
});
